import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { CatchHubLogStore } from '../stores/catchHubLog.store';
import { ProjectStore } from '../stores/projects.store';
import { UserStore } from '../stores/user.store';
import { WorkspaceStore } from '../stores/workspace.store';
import { UserService } from '../user.service';
import { IdHelper } from './id.helper';
import { IntelliCatchItemContextService } from '../intellicatch-item-context.service';
import { CatchHubLogModel } from '../../models/catchHubLog/catchHubLog.model';
import { CatchHubEventType, CatchHubLogType } from '../../models/catchHubLog/catchHubLog.enum';
import { IIntelliCatchGenerationFailurePayload } from '../../models/copilot/intellicatch-generation.model';
import { IIntelliCatchItemSession } from '../../models/copilot/intellicatch-item-context.model';
import * as UiMessageConstants from '../../modules/catch-core/data/constants/ui-message.constants';

/**
 * Writes IntelliCatch failures to the admin Logs screen, in the words an administrator can act on.
 *
 * Logging is a side effect of a failure the user has already been told about, so it must never turn
 * into a second failure. Every entry point swallows its own errors, and the write goes through the
 * store's quiet path, so a POST that fails neither shows the user an error toast nor goes unhandled.
 * A write that cannot reach the server at all - the lost-connection case - is lost, with a console error.
 */
@Injectable({
  providedIn: 'root',
})
export class IntelliCatchLogHelper implements OnDestroy {
  /** The signed-in user's id as the store reports it, which the chat window's own rows fall back to as well. */
  private myUserId = '';
  private myUserSubscription: Subscription | null = null;

  constructor(
    private catchHubLogStore: CatchHubLogStore,
    private userService: UserService,
    private userStore: UserStore,
    private projectStore: ProjectStore,
    private workspaceStore: WorkspaceStore,
    private idHelper: IdHelper,
    private itemContextService: IntelliCatchItemContextService,
  ) {
    this.myUserSubscription =
      this.userStore?.$myUserInfo?.subscribe((userInfo) => {
        if (userInfo) {
          this.myUserId = userInfo.id;
        }
      }) ?? null;
  }

  ngOnDestroy(): void {
    this.myUserSubscription?.unsubscribe();
    this.myUserSubscription = null;
  }

  /**
   * Records that the widget gave up generating a reply. `sessionId` names the item the reply was for;
   * blank, or one this conversation no longer knows, is logged as a general chat failure. `payload`
   * arrives over postMessage, so every field of it is coerced. A message with no payload at all says
   * nothing that could be recorded as a failure, so it is warned about and ignored.
   */
  public logGenerationFailure(sessionId: string | null | undefined, payload: unknown): void {
    if (!payload || typeof payload !== 'object') {
      console.warn('IntelliCatch: ignored a generationFailed message with no payload.', typeof payload);
      return;
    }
    try {
      const failure = this.toGenerationFailure(payload);
      const session = this.itemContextService.getSession(sessionId);
      const reason = UiMessageConstants.intelliCatchGenerationFailureReason(failure.code, failure.message, failure.retryable, failure.attempts);
      this.write(UiMessageConstants.intelliCatchGenerateFailedLogMessage(this.itemName(session), reason), session, CatchHubLogType.Error);
    } catch (err) {
      console.error('IntelliCatch: could not record the generation failure on the Logs screen.', err);
    }
  }

  /**
   * Records that suggested tests could not be added. `message` is the sentence the user was shown and
   * `cause` what went wrong underneath it, when known. A failure is an Error; a refusal the app made
   * itself, with nothing attempted, is passed as a Warning so the Errors stay the failures.
   */
  public logCreateFailure(
    sessionId: string | null | undefined,
    message: string | null | undefined,
    cause: string | null | undefined,
    logType: CatchHubLogType = CatchHubLogType.Error,
  ): void {
    try {
      const session = this.itemContextService.getSession(sessionId);
      const reason = UiMessageConstants.intelliCatchCreateFailureReason(message, cause);
      this.write(UiMessageConstants.intelliCatchAddFailedLogMessage(this.itemName(session), reason), session, logType);
    } catch (err) {
      console.error('IntelliCatch: could not record the create failure on the Logs screen.', err);
    }
  }

  private write(description: string, session: IIntelliCatchItemSession | null, logType: CatchHubLogType): void {
    const currentUser = this.userService.getCurrentUser();
    const timestamp = new Date().toISOString();
    const userId = currentUser?.id || this.myUserId || this.userStore.logUser?.id || UiMessageConstants.INTELLICATCH_LOG_USER_UNKNOWN;
    const userName = currentUser?.emailAddress || currentUser?.userName || this.userStore.logUser?.emailAddress || UiMessageConstants.INTELLICATCH_LOG_USER_UNKNOWN;

    // An item's own project and label when it has a session - the user may have moved on since, and
    // the current ones would then name somewhere the item is not. Otherwise where the user is now.
    const selectedProject = this.projectStore.selectedProject;
    const selectedLabel = this.workspaceStore.selectedWorkspaceInstance;
    const project = session
      ? { id: session.projectId, key: session.projectKey, name: session.projectName }
      : { id: selectedProject?.id ?? '', key: selectedProject?.key ?? '', name: selectedProject?.name ?? '' };
    const label = session ? { key: session.workspaceKey, name: session.workspaceName } : { key: selectedLabel?.key ?? '', name: selectedLabel?.name ?? '' };

    this.catchHubLogStore.addLogsQuietly([
      new CatchHubLogModel(
        this.idHelper.generateNewGUID24(),
        [description, UiMessageConstants.intelliCatchLogContext(project, label)].filter((part) => !!part).join(' '),
        timestamp,
        timestamp,
        userId,
        userId,
        project.name,
        project.id,
        CatchHubEventType.IntelliCatch,
        session?.context?.key ?? '',
        '',
        logType,
        userName,
      ),
    ]);
  }

  private itemName(session: IIntelliCatchItemSession | null): string {
    return session?.context ? UiMessageConstants.intelliCatchLogItemName(session.context.key, session.context.summary) : '';
  }

  private toGenerationFailure(payload: unknown): IIntelliCatchGenerationFailurePayload {
    const candidate = (payload ?? {}) as Record<string, unknown>;
    const attempts = Math.floor(Number(candidate.attempts));
    return {
      code: typeof candidate.code === 'string' ? candidate.code.trim() : '',
      message: typeof candidate.message === 'string' ? candidate.message.trim() : '',
      retryable: candidate.retryable === true,
      attempts: Number.isFinite(attempts) && attempts >= 1 ? attempts : 1,
    };
  }
}
